import {
  Avatar,
  AvatarUserType,
  ChakraBox as Box,
  ChakraFlex as Flex,
  RechartTooltipProps as TooltipProps,
  Text,
  shadows,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { AssessmentCardRiskLevelTag } from './AssessmentCardRiskLevelTag';
import { AssessmentChartLabelToValueMap } from './AssessmentChartLabelToValueMap';
import { AssessmentUnchartedKeys } from './AssessmentChartUnchartedKeys';

export interface AssessmentChartTooltipProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extends TooltipProps<any, any> {
  primaryScoreKey?: string;
  /**
   * When true, numeric scores pad to two decimals and the value column
   * reserves a 40px minimum so multi-subscale rows align vertically.
   * When false/undefined, scores render at stored precision.
   */
  padScoresToTwoDecimals?: boolean;
}

const formatScoreValue = (value: unknown, pad: boolean): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return pad ? value.toFixed(2) : String(value);
  if (typeof value === 'string') return value;
  return '-';
};

const AssessmentChartTooltip = ({
  active,
  label,
  padScoresToTwoDecimals = false,
  payload,
  primaryScoreKey,
}: AssessmentChartTooltipProps) => {
  if (active && payload && payload.length > 0) {
    // we do not ever expect the completedBy value to be a number so a cast here is fine
    // also, the inner payload resembles the AssessmentChartLabelToValueMap type
    const innerPayload = payload[0]
      .payload as Partial<AssessmentChartLabelToValueMap>;
    const completedBy = (innerPayload?.completedOrUpdatedBy || '') as string;
    const completedByType = (innerPayload?.completedOrUpdatedByUserType ??
      'patient') as AvatarUserType;
    const avatarUrl =
      innerPayload?.completedOrUpdatedByUserAvatarSrc || undefined;

    const primaryScoreData = primaryScoreKey
      ? payload.find((item) => String(item.dataKey) === primaryScoreKey)
      : undefined;
    const seenDataKeys = new Set<string>();
    const subscaleRows = payload.filter((item) => {
      const dataKey = String(item.dataKey);
      if (
        (AssessmentUnchartedKeys as string[]).includes(dataKey) ||
        dataKey === primaryScoreKey ||
        seenDataKeys.has(dataKey)
      ) {
        return false;
      }
      seenDataKeys.add(dataKey);
      return true;
    });
    const orderedRows = primaryScoreData
      ? [primaryScoreData, ...subscaleRows]
      : subscaleRows;
    // Single-score charts (total-only, no subscales) don't need a fixed
    // value column since there's nothing to align with — let the label
    // breathe to avoid cutting off text like "Total score" when a tag is
    // present. Multi-subscale charts keep a fixed value column so values
    // align vertically across rows.
    const reserveValueColumn = padScoresToTwoDecimals;

    return (
      <Box
        background="neutral.background.primary"
        borderRadius="8px"
        boxShadow={shadows.medium}
        padding="12px"
        width="max-content"
        minWidth="220px"
      >
        <Flex
          alignItems="center"
          gap="18px"
          justifyContent="space-between"
          marginBottom="12px"
        >
          <Text color="neutral.secondary" textAlign="left" variant="caption">
            {DateTime.fromMillis(label).toFormat('LL/dd/yyyy')}
          </Text>
          {completedBy !== '' && (
            <Avatar
              name={completedBy}
              size="2xs"
              src={avatarUrl}
              userType={completedByType}
            />
          )}
        </Flex>

        <Flex direction="column" gap="8px">
          {orderedRows.map((item) => {
            const dataKey = String(item.dataKey);
            const isPrimaryScore = dataKey === primaryScoreKey;
            return (
              <Flex
                alignItems="center"
                data-testid={
                  isPrimaryScore
                    ? 'chart-tooltip-global-row'
                    : `chart-tooltip-row-${dataKey}`
                }
                gap="24px"
                key={`tooltip-${label}-${dataKey}`}
              >
                <Flex alignItems="center" flex="1" gap="6px" minWidth={0}>
                  <Box
                    backgroundColor={item.stroke}
                    borderRadius="200px"
                    data-testid={`chart-tooltip-color-${dataKey}`}
                    flexShrink={0}
                    height="10px"
                    width="10px"
                  />
                  <Text
                    color="neutral.primary"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant={isPrimaryScore ? 'captionMedium' : 'caption'}
                    whiteSpace="nowrap"
                  >
                    {dataKey}
                  </Text>
                </Flex>
                <Flex alignItems="center" flexShrink={0} gap="12px">
                  {isPrimaryScore && innerPayload?.riskLevel && (
                    <Box flexShrink={0}>
                      <AssessmentCardRiskLevelTag
                        id={`tooltip-${label}`}
                        riskLevel={innerPayload.riskLevel}
                      />
                    </Box>
                  )}
                  <Text
                    color="neutral.primary"
                    minWidth={reserveValueColumn ? '40px' : undefined}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    textAlign="right"
                    variant="captionMedium"
                    whiteSpace="nowrap"
                  >
                    {formatScoreValue(item.value, padScoresToTwoDecimals)}
                  </Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Box>
    );
  }
  return null;
};

export default AssessmentChartTooltip;
